import { api } from "api/api";
import { AxiosError } from "axios";
import Pessoa from "model/administracao/cadastro/Pessoa";
import Produto from "model/administracao/cadastro/produto/Produto";
import Page from "model/page/Page";
import Pageable from "model/page/Pageable";
import EmprestimoPatrimonio from "model/patrimonio/EmprestimoPatrimonio";
import LoteItemEmprestimo from "model/patrimonio/LoteItemEmprestimo";
import Patrimonio from "model/patrimonio/Patrimonio";
import Deposito from "model/suprimentos/Deposito";
import Estoque from "model/suprimentos/Estoque";
import EstoqueLote from "model/suprimentos/EstoqueLote";
import { DataTableFilterMeta } from "primereact/datatable";
import { BaseServiceCadastros } from "service/BaseServiceCadastros";
import { getFilters } from "utils/filterUtils";
import { generateKey } from "utils/generateKey";

class EmprestimoPatrimonioService implements BaseServiceCadastros<EmprestimoPatrimonio> {
    
    pesquisar(page: Pageable,  filters: DataTableFilterMeta) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/emprestimoPatrimonio/pesquisar`;
            url += getFilters(filters);
            const params = {
                page: page.page,
                size: page.size,
                sort: page.sort,
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => {reject(err.response);
                    console.error('Error status:', err.response?.status);
                    console.error('Error details:', err.response?.data);
                })
        });
    }

    consultar(id: number) {
        console.log("ID recebido:", id);
        return new Promise<EmprestimoPatrimonio>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/consultar/${id}`)
                .then(response => {
                    const emprestimoPatrimonio: EmprestimoPatrimonio = response.data;
                    emprestimoPatrimonio.itens.forEach(item => {
                        item.key = generateKey();
                        return item;
                    });
                    resolve(emprestimoPatrimonio);
                })
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    incluir(emprestimoPatrimonio: EmprestimoPatrimonio) {
        return new Promise<EmprestimoPatrimonio>((resolve, reject): void => {
            api
                .post(`/emprestimoPatrimonio/incluir`, JSON.stringify(emprestimoPatrimonio))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    editar(emprestimoPatrimonio: EmprestimoPatrimonio) {
        return new Promise<EmprestimoPatrimonio>((resolve, reject): void => {
            api
                .put(`/emprestimoPatrimonio/editar`, JSON.stringify(emprestimoPatrimonio))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    excluir(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .delete(`/emprestimoPatrimonio/excluir/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    emprestar(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .put(`/emprestimoPatrimonio/emprestar/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    devolvido(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .put(`/emprestimoPatrimonio/devolvido/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    cancelar(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .put(`/emprestimoPatrimonio/cancelar/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    pessoas() {
        return new Promise<Pessoa[]>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/pessoas`)
                .then((response) => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    patrimonios() {
        return new Promise<Patrimonio[]>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/patrimonios`)
                .then((response) => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    emprestimos() {
        return new Promise<EmprestimoPatrimonio[]>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/allComItens`)
                .then((response) => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    depositos() {
        return new Promise<Deposito[]>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/depositos`)
                .then((response) => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        })
    }

    produtos() {
        return new Promise<Produto[]>((resolve, reject): void => {
            api
                .get(`/emprestimoPatrimonio/produtos`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    lotesDisponiveis(produto: Produto): Promise<LoteItemEmprestimo[]> {
        const url = `/emprestimoPatrimonio/lotesDisponiveis/${encodeURIComponent(produto.codigo as string)}`;
        return new Promise<LoteItemEmprestimo[]>((resolve, reject): void => {
            api
                .get(url)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    sugerirLotes(produto: Produto, quantidade: number){
        const url = `/emprestimoPatrimonio/lotesSugeridos/${encodeURIComponent(produto.codigo as string)}/${encodeURIComponent(quantidade)}`;
        return new Promise<LoteItemEmprestimo[]>((resolve, reject): void => {
            api
                .get(url)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getStatusLista(){
        return [{
                value: "CRIADO",
                label: "Criado"
            },
            {
                value: "EMPRESTADO",
                label: "Emprestado"
            },
            {
                value: "DEVOLVIDO",
                label: "Devolvido"
            },
            {
                value: "CANCELADO",
                label: "Cancelado"
            }]
    }

    getStatus(status: string): string {
        let statusObj = this.getStatusLista().find((item) => item.value === status)
        if (statusObj) {
            return statusObj.label
        }
        return "";
    }

    getListaStatusFilter() {
        return [
            { label: "Criado", value: "C" },
            { label: "Emprestado", value: "E" },
            { label: "Devolvido", value: "D" },
            { label: "Cancelado", value: "X" }
        ]
    }

    relatorioEmprestimo(id: number) {
        return new Promise<string>((resolve, reject): void => {
            let url = `/emprestimoPatrimonio/relatorioEmprestimo/${id}`;
            api
                .get(url, {responseType: 'arraybuffer'})
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }
}

export default EmprestimoPatrimonioService;